import { ReactNode, useMemo } from 'react';
import { useLocation } from 'react-router';

export interface BreadcrumbMatch {
  pathname: string;
  breadcrumb: ReactNode;
}

// Helper function to build breadcrumb trail by traversing up the route hierarchy
const buildBreadcrumbTrail = (
  routes: any[],
  pathname: string,
  parentPath = '',
): BreadcrumbMatch[] => {
  const breadcrumbs: BreadcrumbMatch[] = [];

  for (const route of routes) {
    let fullPath = parentPath + (route.path || '');

    // Handle special cases for path construction
    if (parentPath === '/' && route.path && route.path.startsWith('/')) {
      // If parent is '/' and child path starts with '/', use child path directly
      fullPath = route.path;
    } else if (
      parentPath === '/' &&
      route.path &&
      !route.path.startsWith('/')
    ) {
      // If parent is '/' and child path doesn't start with '/', prepend '/'
      fullPath = '/' + route.path;
    } else if (parentPath !== '/' && route.path && route.path.startsWith('/')) {
      // If parent is not '/' and child path starts with '/', use child path directly
      fullPath = route.path;
    }

    // Check if this route matches the current pathname
    const isExactMatch = fullPath === pathname;
    const isParentMatch =
      fullPath !== '/' && pathname.startsWith(fullPath + '/');
    const isRootMatch = fullPath === '/' && pathname === '/';
    const isRootParent = fullPath === '/' && pathname !== '/';

    if (isExactMatch || isParentMatch || isRootMatch || isRootParent) {
      // Add this route to breadcrumbs if it has a breadcrumb
      if (route.breadcrumb !== undefined && route.breadcrumb !== null) {
        let breadcrumbText: ReactNode = route.breadcrumb;

        // If breadcrumb is a function, call it with route info
        if (typeof route.breadcrumb === 'function') {
          breadcrumbText = route.breadcrumb({ pathname: fullPath });
        }

        breadcrumbs.push({
          pathname: fullPath,
          breadcrumb: breadcrumbText,
        });
      }

      // Recursively check children
      if (route.children) {
        breadcrumbs.push(
          ...buildBreadcrumbTrail(route.children, pathname, fullPath),
        );
      }
    }
  }

  return breadcrumbs;
};

export const useBreadcrumbs = (
  routes: any[],
  options: { excludePaths?: string[] } = {},
): BreadcrumbMatch[] => {
  const location = useLocation();
  const { excludePaths = [] } = options;

  return useMemo(() => {
    const breadcrumbs: BreadcrumbMatch[] = [];
    const { pathname } = location;

    // Skip excluded paths
    if (excludePaths.includes(pathname)) {
      return breadcrumbs;
    }

    // Build breadcrumb trail by traversing the route hierarchy
    const trail = buildBreadcrumbTrail(routes, pathname);

    // Filter out excluded paths and ensure we have valid breadcrumbs
    return trail.filter(
      (breadcrumb) =>
        !excludePaths.includes(breadcrumb.pathname) &&
        breadcrumb.breadcrumb !== null,
    );
  }, [location.pathname, routes, excludePaths]);
};
